import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { orderAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { REFUND_CLOSING_SOON_DAYS } from '../../utils/refundTimeline';


const BuyerPanelAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const email = user?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email;
    if (!email) return;

    const load = async () => {
      try {
        const response = await orderAPI.searchOrders({
          page: 0,
          size: 100,
          userEmail: email,
          sortBy: 'creationDate',
          sortDirection: 'DESC',
        });
        const orders = response.data?.data || [];
        const next = [];

        orders.forEach((order) => {
          const status = order.orderStatus;
          const timeline = order.refundTimeline;

          if (status === 'REJECTED') {
            next.push({
              id: `rejected-${order.id}`,
              type: 'danger',
              title: `Order ${order.orderId} rejected`,
              message: order.reason ? `Reason: ${order.reason}` : 'Please check details or contact support.',
              link: '/buyer/orders',
            });
          }

          if (status === 'ORDER_FORM_REFILL_REQUEST') {
            next.push({
              id: `order-refill-${order.id}`,
              type: 'warning',
              title: `Refill order form — ${order.orderId}`,
              message: order.reason ? `Reason: ${order.reason}` : 'Please refill your order form.',
              link: `/order?edit=${order.id}&refill=true`,
            });
          }

          if (status === 'REFUND_FORM_REFILL_REQUEST' || status === 'REJECT_AND_REFILL') {
            next.push({
              id: `refund-refill-${order.id}`,
              type: 'warning',
              title: status === 'REJECT_AND_REFILL'
                ? `Reject and refill refund form — ${order.orderId}`
                : `Refill refund form — ${order.orderId}`,
              message: order.reason
                ? `Reason: ${order.reason}`
                : 'Please refill your refund form with the corrected details.',
              link: `/refund?edit=${order.id}&refill=true&orderId=${order.orderId}`,
            });
          }

          if (['ORDER_FORM_FILLED', 'ORDER_FORM_REFILL_REQUEST'].includes(status) && timeline?.refundFormOpen) {
            const daysLeft = timeline.daysLeftToClose;
            if (daysLeft != null && daysLeft > 0 && daysLeft <= REFUND_CLOSING_SOON_DAYS) {
              next.push({
                id: `closing-${order.id}`,
                type: 'danger',
                title: `Refund deadline in ${daysLeft} day(s) — ${order.orderId}`,
                message: timeline.warningMessage
                  || `Fill your refund form before it closes. ${daysLeft} day(s) remaining.`,
                link: `/refund?orderId=${order.orderId}`,
              });
            } else if (['PENALTY_50', 'PENALTY_100'].includes(timeline.warningLevel)) {
              next.push({
                id: `penalty-${order.id}`,
                type: 'danger',
                title: `Refund overdue — ${order.orderId}`,
                message: timeline.warningMessage || 'Penalty applies. Fill refund form now.',
                link: `/refund?orderId=${order.orderId}`,
              });
            }
          }
        });

        setAlerts(next);
      } catch {
        setAlerts([]);
      }
    };

    load();
  }, [user?.email]);

  if (alerts.length === 0) return null;

  const styles = {
    danger: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b', icon: 'alert-circle' },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', icon: 'alert-triangle' },
  };

  return (
    <div style={{ padding: '12px 24px 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {alerts.map((alert) => {
        const style = styles[alert.type] || styles.warning;
        return (
          <div
            key={alert.id}
            style={{
              padding: '14px 18px',
              borderRadius: '12px',
              background: style.bg,
              border: `1px solid ${style.border}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <Icon name={style.icon} size={22} color={style.color} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: style.color }}>{alert.title}</div>
              <div style={{ fontSize: '13px', color: style.color, marginTop: '4px', opacity: 0.9 }}>{alert.message}</div>
              {alert.link && (
                <Link
                  to={alert.link}
                  style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', fontWeight: 600, color: style.color }}
                >
                  Take action →
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BuyerPanelAlerts;
